import "./TeamPageV2.scss";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  DownOutlined,
  EllipsisOutlined,
  ExclamationCircleFilled
} from "@ant-design/icons";
import {Button, Drawer, Dropdown, Menu, Table} from "antd";
import {
  LocationDto,
  MemberDto,
  MemberDtoRoleEnum,
  MemberLocationPermissionDto
} from "../../../../generated/dashboard-client";
import {locationsState, memberState, mobileState} from "../../../../state/atoms";
import {useRecoilValue} from "recoil";
import {useEffect, useState} from "react";
import {Modal} from "antd";
import CheckIcon from "../../../icons/CheckIcon";
import {levelLabel} from "./permissionModel";

export enum UsersTableType {
  Active, Pending, Inactive
}

interface TeamPageActiveUsersProps {
  type: UsersTableType;
  users: MemberDto[];
  onMenuOptionClick: (option: string, member: MemberDto) => void;
  onSortClick: (sort: string) => void;
  pagination?: any;
  onChangeRoleClick: (member: MemberDto, role: MemberDtoRoleEnum) => void;
  userPermissions?: Record<string, MemberLocationPermissionDto[]>;
  resentIds?: Set<string>;
}

function getPermissionLabelsForLocation(member: MemberDto, owner: MemberDto, permissions: MemberLocationPermissionDto): string {
  const labels = [];

  if (permissions.viewOnly) labels.push(levelLabel("viewOnly"));
  if (permissions.dataOnly || permissions.itemLibraryOnly) labels.push(levelLabel("editMenuData"));
  if (permissions.downloadAndPublish) labels.push(levelLabel("editDesigns"));
  if (permissions.createTemplate) labels.push("Create Templates");
  if ((permissions as any).publish) labels.push("Publish");
  if (permissions.orderPrint) labels.push("Order Print");
  if ((permissions as any).manageBrand) labels.push("Manage Brand");
  if (owner.diningRDIntegration && permissions.updateDrd) labels.push("Data Only");

  return labels.length > 0 ? labels.join(", ") : "Limited Access";
}

interface LocationPermissionsProps {
  permissions: MemberLocationPermissionDto[];
  locations: LocationDto[];
  owner: MemberDto;
  member: MemberDto;
}

function LocationPermissions({permissions, locations, member, owner}: LocationPermissionsProps) {
  const [expanded, setExpanded] = useState(false);
  const locationMap = new Map(locations.map(l => [l.internalId, l.name]));
  const sortedPermissions = [...permissions].sort((a, b) => {
    const nameA = locationMap.get(a.locationId!) || '';
    const nameB = locationMap.get(b.locationId!) || '';
    return nameA.localeCompare(nameB);
  });
  const displayPerms = expanded ? sortedPermissions : sortedPermissions.slice(0, 3);

  return (
    <div>
      {displayPerms.map((permission, i) => {
        const locationName = locationMap.get(permission.locationId!) || `Location ${permission.locationId}`;
        return (
          <div key={permission.locationId ?? i}>
            <strong>{locationName}:</strong> {getPermissionLabelsForLocation(member, owner, permission)}
          </div>
        );
      })}
      {permissions.length > 3 && !expanded && (
        <button onClick={() => setExpanded(true)} className="link-button">Show more +</button>
      )}
      {permissions.length > 3 && expanded && (
        <button onClick={() => setExpanded(false)} className="link-button">Show less -</button>
      )}
    </div>
  );
}

export function UsersTable({
                             type,
                             users,
                             onMenuOptionClick,
                             onChangeRoleClick,
                             onSortClick,
                             pagination,
                             userPermissions,
                             resentIds
                           }: TeamPageActiveUsersProps) {
  const member = useRecoilValue(memberState);
  const mobile = useRecoilValue(mobileState);
  const locations = useRecoilValue(locationsState);
  const [mobileSortDrawer, setMobileSortDrawer] = useState(false);
  const [mobileOptionsDrawers, setMobileOptionsDrawers] = useState([] as boolean[]);

  useEffect(() => {
    let array = new Array(users.length).fill(false);
    setMobileOptionsDrawers([...array]);
  }, [users.length]);

  function setOpenMobileOptionsDrawer(index: number) {
    let array = new Array(users.length).fill(false);
    array[index] = true;
    setMobileOptionsDrawers([...array]);
  }

  function setClosedMobileOptionsDrawers() {
    let array = new Array(users.length).fill(false);
    setMobileOptionsDrawers([...array]);
  }

  const menuSortItems = [
    {
      key: "asc",
      label: "Alphabetical (A-Z)",
      icon: <ArrowUpOutlined style={{fontSize: "18px"}}/>,
    },
    {
      key: "desc",
      label: "Alphabetical (Z-A)",
      icon: <ArrowDownOutlined style={{fontSize: "18px"}}/>,
    },
  ]

  const userMenuOptionItems = (type === UsersTableType.Active || type === UsersTableType.Pending) ? [
    {
      key: "edit-locations",
      label: "Edit Locations",
    },
    {
      key: "edit-permissions",
      label: "Edit Permissions",
    },
  ] : [
    {
      key: "activate-account",
      label: "Activate Account",
    },
    {
      key: "permanently-remove",
      label: "Permanently Remove",
    },
  ];

  if (type === UsersTableType.Active) {
    userMenuOptionItems.push({
      key: "remove-access",
      label: "Remove Access",
    });
  } else if (type === UsersTableType.Pending) {
    userMenuOptionItems.push({
      key: "resend-invite",
      label: "Resend Invite",
    });
    userMenuOptionItems.push({
      key: "cancel-invite",
      label: "Cancel",
    });
  }

  function getMenuOptionItems(member: MemberDto) {
    if (member.role === "User") {
      return userMenuOptionItems;
    } else if (member.role === "Admin") {
      if (type === UsersTableType.Active) {
        return [{
          key: "remove-access",
          label: "Remove Access",
        }]
      } else if (type === UsersTableType.Pending) {
        return [{
          key: "resend-invite",
          label: "Resend Invite",
        },
          {
            key: "cancel-invite",
            label: "Cancel",
          }];
      } else {
        return [
          {
            key: "activate-account",
            label: "Activate Account",
          },
          {
            key: "permanently-remove",
            label: "Permanently Remove",
          },
        ]
      }
    }
  }

  const columns = [
    {
      title: () => (
        mobile ?
          <>
            <span style={{cursor: "pointer"}}>
              Name <DownOutlined onClick={() => setMobileSortDrawer(true)}
                                 style={{fontSize: "14px", transform: "translate(0, 2px)", marginLeft: "4px"}}/>
            </span>
            <Drawer
              open={mobileSortDrawer}
              placement="bottom"
              onClose={() => setMobileSortDrawer(false)}
              title={"Sort by"}
              className="mobile-users-drawer"
              closeIcon={<DownOutlined/>}
              height={230}
            >
              <Menu
                items={menuSortItems}
                onClick={({key}) => {
                  onSortClick(key);
                  setMobileSortDrawer(false);
                }}
              />
            </Drawer>
          </>
          :
          <Dropdown
            trigger={["click"]}
            menu={{
              items: menuSortItems,
              onClick: ({key}) => onSortClick(key)
            }}
          >
            <span style={{cursor: "pointer"}}>
              Name <DownOutlined style={{fontSize: "14px", transform: "translate(0, 2px)", marginLeft: "4px"}}/>
            </span>
          </Dropdown>
      ),

      dataIndex: "name",
      key: "name",
      render: (_: any, record: MemberDto) => (
        <div className={"flex"}>
          {type === UsersTableType.Active && <CheckCircleFilled/>}
          {type === UsersTableType.Pending && <ExclamationCircleFilled/>}
          {type === UsersTableType.Inactive && <CloseCircleFilled/>}
          <div className="name-email-mob">
            <span>{record.name}</span>
            {mobile && <span>{record.email}</span>}
          </div>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (_: any, record: MemberDto) => (
        <span className={"email-cell"}>
          {record.email}
          {record.id && resentIds?.has(record.id) &&
            <span className={"invite-resent-badge"}><CheckOutlined /> Invite resent</span>}
        </span>
      ),
    },
    {
      title: "Role",
      key: "role",
      render: (_: any, record: MemberDto) => {
        if (record.role === "Owner" || record.id === member.id) return record.role;
        return <Dropdown
          dropdownRender={() => <ul
            className="ant-dropdown-menu ant-dropdown-menu-root ant-dropdown-menu-vertical ant-dropdown-menu-light team-role-drop">
            <li onClick={() => onChangeRoleClick(record, "User")}>
              User
              {record.role === "User" && <CheckIcon/>}
            </li>
            <li onClick={() => onChangeRoleClick(record, "Admin")}>
              Admin
              {record.role === "Admin" && <CheckIcon/>}
            </li>
            <li className={"legend"}>
              <p>Admins have full access to all features, user management, and all locations.<br/>NOTE: Only the Account
                Owner can manage billing and subscriptions.</p>
            </li>
          </ul>}
        >
          <Button className={"no-btn"}>
            {record.role} <DownOutlined style={{fontSize: "14px", transform: "translate(0, 2px)", marginLeft: "4px"}}/>
          </Button>
        </Dropdown>
      }
    },
    {
      title: "Permissions",
      key: "permissions",
      render: (_: any, record: MemberDto) => {
        if (record.role !== "User") return <span>Full Access</span>;
        const perms = record.id && userPermissions?.[record.id];

        if (perms && perms.length > 0) return <LocationPermissions
          member={record}
          permissions={perms}
          locations={locations}
          owner={member}
        />;

        return <span>Limited Access</span>;
      }
    },
    {
      title: "",
      key: "actions",
      render: (_: any, record: MemberDto, index: number) => {
        if (record.role === "Owner") return null;
        if (record.id === member.id) return null;

        if (mobile) {
          return <>
            <Button className={"user-set-btn"} onClick={() => setOpenMobileOptionsDrawer(index)}>
              <EllipsisOutlined/>
            </Button>
            <Drawer
              open={mobileOptionsDrawers[index]}
              placement="bottom"
              onClose={() => setClosedMobileOptionsDrawers()}
              title={"More"}
              className="mobile-users-drawer"
              closeIcon={<DownOutlined/>}
              height={270}
            >
              <Menu
                items={getMenuOptionItems(record)}
                onClick={({key}) => onMenuOptionClick(key, record)}
              />
            </Drawer>
          </>
        } else {
          return <Dropdown
            menu={{
              items: getMenuOptionItems(record),
              onClick: ({key}) => {
                if (["remove-access", "cancel-invite", "permanently-remove", "activate-account"].indexOf(key) > -1) {
                  Modal.confirm({
                    title: "Are you sure?",
                    onOk() {
                      onMenuOptionClick(key, record);
                    },
                  });
                } else {
                  onMenuOptionClick(key, record);
                }
              },
            }}
          >
            <Button className={"user-set-btn"}>
              <EllipsisOutlined/>
            </Button>
          </Dropdown>
        }
      },
    }
  ];

  return (
    <Table
      rowKey={(record) => record.id!}
      columns={columns}
      dataSource={users}
      pagination={pagination || false}
    />
  );
}
